import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft } from 'lucide-react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { SortingOptionLabel } from 'components/SortSelect/SortingOptionLabel'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { MobileInfiniteScrollView } from 'components/MobileInfiniteScrollView'
import { MobileReminderCard } from 'components/Reminders/MobileReminderCard'
import { MobileColors } from 'styles/mobileTokens'
import { FbLink } from 'components/FbUI/FbLink'
import apiService from 'services/api'
import { ReminderListItem } from 'models/reminders'
import { PaginatedResponse } from 'models/paginated-response'
import { BackButton, COLORS } from 'components/Mobile/Mobile.styles'

const REMINDERS_TABLE_KEY = 'mobile-reminders-table'

const sortingOptions = [
  {
    label: <SortingOptionLabel label="Date Created" type="number" />,
    value: 'created_at',
  },
  {
    label: <SortingOptionLabel label="Date Created" type="number" desc />,
    value: '-created_at',
  },
  {
    label: <SortingOptionLabel label="Scheduled" type="number" />,
    value: 'run_at',
  },
  {
    label: <SortingOptionLabel label="Scheduled" type="number" desc />,
    value: '-run_at',
  },
  {
    label: <SortingOptionLabel label="Title" />,
    value: 'title',
  },
  {
    label: <SortingOptionLabel label="Title" desc />,
    value: '-title',
  },
]

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${COLORS.lightGray};
`

const HeaderSection = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #191d23;
`

const CompanyLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`

const ShowInactiveContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 8px;
  font-size: 14px;
  color: #374151;
`

export function MobileRemindersPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const api = apiService()

  const companyId = searchParams.get('contact_company_id')
  const doorId = searchParams.get('door_id')
  const companyName = searchParams.get('company_name')
  const doorName = searchParams.get('door_name')

  const [showInactive, setShowInactive] = useState<boolean>(false)

  // expanded card (only one at a time)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const IDENTIFIER = 'MOBILE_REMINDERS_TABLE'

  const sortingStore = dataTableSortingStoreRepo.getStore(REMINDERS_TABLE_KEY)()
  const remindersFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const filterStore = remindersFilterStore()
  const { page, setPage } = filterStore
  const { sorting } = sortingStore

  const filtersAsParams = {
    ...useFilterParams(remindersFilterStore),
  }

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }
    if (companyId) params['contact_company'] = companyId
    if (doorId) params['door_proxy'] = doorId
    return params
  }, [filtersAsParams, companyId, doorId])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  useEffect(() => {
    if (page > 1) {
      setPage(1)
    }
  }, [showInactive, page, setPage])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
      ...(showInactive
        ? {}
        : { status__exclude: ['sent', 'ended', 'deleted', 'paused'] }),
    }),
    [filterParams, sortParams, showInactive]
  )

  const query = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['reminders-infinite', filterAndSortParams],
    queryFn: ({ pageParam = 1 }) =>
      api.getReminders({ ...filterAndSortParams, page: pageParam, limit: 10 }),
    getNextPageParam: (
      lastPage: PaginatedResponse<ReminderListItem>,
      _allPages,
      lastPageParam
    ) => {
      const totalPages = Math.ceil(lastPage.count / 10)
      return lastPageParam < totalPages ? lastPageParam + 1 : undefined
    },
  })

  const handleBack = () => {
    navigate(-1)
  }

  const handleEditReminder = (reminderId: number) => {
    navigate(`/reminders/${reminderId}/edit`)
  }

  const toggleExpanded = (id: number) => {
    setExpandedId((current) => (current === id ? null : id))
  }

  return (
    <PageContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft size={20} />
        Back
      </BackButton>

      <HeaderSection>
        <PageTitle>Reminders</PageTitle>
        {(companyName || doorName) && (
          <CompanyLinks>
            {companyName && companyId && (
              <FbLink to={`/company/${companyId}`}>{companyName}</FbLink>
            )}
            {doorName && doorId && (
              <>
                {companyName && <span>·</span>}
                <FbLink to={`/door/${doorId}`}>{doorName}</FbLink>
              </>
            )}
          </CompanyLinks>
        )}
      </HeaderSection>

      <MobileListViewHeader
        sortingOptions={sortingOptions}
        filterStoreIdentifier={IDENTIFIER}
        tableKey={REMINDERS_TABLE_KEY}
        defaultSorting={[{ id: 'created_at', desc: true }]}
      />

      <ShowInactiveContainer>
        <input
          type="checkbox"
          id="showInactive"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        <label htmlFor="showInactive">Show Inactive</label>
      </ShowInactiveContainer>

      <FilterChips
        store={remindersFilterStore}
        identifier={IDENTIFIER}
        clearAllButton
        showActive
      />

      <MobileInfiniteScrollView<
        PaginatedResponse<ReminderListItem>,
        ReminderListItem
      >
        extractItems={(page) => page.results}
        infiniteQuery={query}
      >
        {(reminders) =>
          reminders?.map((reminder: ReminderListItem) => (
            <MobileReminderCard
              key={reminder.id}
              reminder={reminder}
              expanded={expandedId === reminder.id}
              onToggleExpand={() => toggleExpanded(reminder.id)}
              onEdit={handleEditReminder}
            />
          ))
        }
      </MobileInfiniteScrollView>
    </PageContainer>
  )
}
